import { useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Checkbox,
  Group,
  Loader,
  Modal,
  Select,
  Stack,
  Table,
  Text,
  Title,
  Tooltip,
} from "@mantine/core";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import { IconAlertTriangle, IconCloudUpload, IconLock } from "@tabler/icons-react";
import {
  apiDelete,
  apiFetch,
  apiPatch,
  apiPost,
  friendlyError,
  type ApiError,
} from "../../api/client.js";
import {
  formatBytes,
  type BackupCardJob,
  type BackupCardState,
  type BackupRowSummary,
} from "./vmUtils.js";

interface BackupsCardProps {
  kind: "qemu" | "lxc";
  vmid: number;
  name?: string;
  refreshKey?: number;
}

const POLL_WHILE_RUNNING_MS = 10_000;

const HOUR_OPTIONS = Array.from({ length: 24 }, (_, h) => {
  const hh = String(h).padStart(2, "0");
  return { value: hh, label: h === 0 ? "00:00 (midnight)" : `${hh}:00` };
});

const MINUTE_OPTIONS = [0, 15, 30, 45].map((m) => {
  const mm = String(m).padStart(2, "0");
  return { value: mm, label: mm };
});

const nzTimeFmt = new Intl.DateTimeFormat(undefined, {
  dateStyle: "medium",
  timeStyle: "short",
  timeZone: "Pacific/Auckland",
});

function formatBackupTime(unixSec?: number | null): string {
  if (!unixSec) return "—";
  return nzTimeFmt.format(new Date(unixSec * 1000));
}

/** Daily clock label for a job, or the raw PVE schedule string when the
 * console-edited schedule isn't a plain HH:MM the picker can represent. */
function scheduleLabel(job: BackupCardJob): string {
  if (job.hour === null || job.minute === null) {
    return job.rawSchedule ?? "an operator-managed schedule";
  }
  return `${String(job.hour).padStart(2, "0")}:${String(job.minute).padStart(2, "0")}`;
}

export function isApiError(err: unknown): err is ApiError {
  return !!err && typeof err === "object" && "status" in err;
}

/*
 * Backups card (Phase 23). Rendered directly below the Disks card on the
 * VM and container detail pages. Backed by ONE PVE cluster backup job per
 * customer — the card shows the job's schedule, this server's membership,
 * the active (portal-dispatched) run, and the PBS volume list. Fetches its
 * own state on mount (and on `refreshKey` change), polls every 10s ONLY
 * while a backup run is active, and clears the interval as soon as the run
 * leaves the running state (or the card unmounts).
 *
 * Enable/join error mapping (matches the backend's response shapes):
 *   201                        -> green notification, close modal, refetch
 *   201 runNowFailed           -> extra yellow notification (job still created)
 *   409 backup_job_exists      -> "This server is already in your backup job"
 *   409 backup_storage_not_configured -> "Backups aren't set up for your account…"
 *   502 pve_forbidden          -> "Backup service isn't fully configured…"
 *   502 pve_unavailable        -> "Proxmox unavailable" notification
 *   else                       -> friendlyError fallthrough
 *
 * Schedule edit: PATCH 200 -> green notification + refetch.
 *
 * Remove error mapping:
 *   204                        -> green notification + refetch
 *   404 backup_job_not_found   -> treated as success (refetch)
 *   else                       -> friendlyError fallthrough
 */
export function BackupsCard({ kind, vmid, name, refreshKey }: BackupsCardProps) {
  const resourceBase = kind === "qemu" ? "vms" : "containers";

  const [state, setState] = useState<BackupCardState | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const [enableOpen, setEnableOpen] = useState(false);
  const [hourSel, setHourSel] = useState("02");
  const [minuteSel, setMinuteSel] = useState("00");
  const [runNow, setRunNow] = useState(false);
  const [enabling, setEnabling] = useState(false);

  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [adding, setAdding] = useState(false);

  async function load(showSpinner: boolean) {
    if (showSpinner) setLoading(true);
    try {
      const data = await apiFetch<BackupCardState>(`/api/${resourceBase}/${vmid}/backups`);
      setState(data);
      setError(false);
    } catch {
      setError(true);
    } finally {
      if (showSpinner) setLoading(false);
    }
  }

  useEffect(() => {
    setState(null);
    setError(false);
    void load(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resourceBase, vmid, refreshKey]);

  const running = state?.activeRun?.status === "running";

  useEffect(() => {
    if (!running) return;
    const id = setInterval(() => void load(false), POLL_WHILE_RUNNING_MS);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [running]);

  function openEnableModal() {
    setHourSel("02");
    setMinuteSel("00");
    setRunNow(false);
    setEnableOpen(true);
  }

  async function applyEnable() {
    setEnabling(true);
    try {
      const res = await apiPost<{ runNowFailed?: boolean }>(
        `/api/${resourceBase}/${vmid}/backups`,
        {
          hour: parseInt(hourSel, 10),
          minute: parseInt(minuteSel, 10),
          ...(runNow ? { run_now: true } : {}),
        }
      );
      notifications.show({
        color: "green",
        message: `Backups enabled — daily at ${hourSel}:${minuteSel}`,
      });
      if (res?.runNowFailed) {
        notifications.show({
          color: "yellow",
          message: "Backup couldn't start now — scheduling is still active",
        });
      }
      setEnableOpen(false);
      void load(false);
    } catch (err) {
      if (!isApiError(err)) {
        notifications.show({ color: "red", message: "Couldn't reach the server." });
        return;
      }
      const mapped = backupActionError(err, "Couldn't enable backups.");
      notifications.show({ color: "red", title: mapped.title, message: mapped.message });
    } finally {
      setEnabling(false);
    }
  }

  async function addToList() {
    if (!state?.job) return;
    setAdding(true);
    try {
      await apiPost(`/api/${resourceBase}/${vmid}/backups`, {
        // hour/minute are only consumed by the backend when it CREATES the
        // job; when joining it sends only {vmid} and keeps the existing
        // schedule. The job's own values are passed so a fresh job would
        // at least match what the banner showed (rawSchedule fallback → 2/0).
        hour: state.job.hour ?? 2,
        minute: state.job.minute ?? 0,
      });
      notifications.show({ color: "green", message: "Server added to your backup job." });
      void load(false);
    } catch (err) {
      if (!isApiError(err)) {
        notifications.show({ color: "red", message: "Couldn't reach the server." });
        return;
      }
      const mapped = backupActionError(err, "Couldn't add this server to backups.");
      notifications.show({ color: "red", title: mapped.title, message: mapped.message });
    } finally {
      setAdding(false);
    }
  }

  function openScheduleModal() {
    const job = state?.job;
    if (!job) return;
    setHourSel(job.hour !== null ? String(job.hour).padStart(2, "0") : "02");
    setMinuteSel(job.minute !== null ? String(job.minute).padStart(2, "0") : "00");
    setScheduleOpen(true);
  }

  async function applySchedule() {
    setSavingSchedule(true);
    try {
      await apiPatch<{ schedule: string }>(`/api/${resourceBase}/${vmid}/backups`, {
        hour: parseInt(hourSel, 10),
        minute: parseInt(minuteSel, 10),
      });
      notifications.show({
        color: "green",
        message: `Schedule updated — daily at ${hourSel}:${minuteSel}`,
      });
      setScheduleOpen(false);
      void load(false);
    } catch (err) {
      if (!isApiError(err)) {
        notifications.show({ color: "red", message: "Couldn't reach the server." });
        return;
      }
      const mapped = backupActionError(err, "Couldn't update the schedule.");
      notifications.show({ color: "red", title: mapped.title, message: mapped.message });
    } finally {
      setSavingSchedule(false);
    }
  }

  function confirmRemove() {
    modals.openConfirmModal({
      title: "Remove from backups?",
      centered: true,
      children: (
        <Text size="sm">
          This server is removed from the backup job and stops receiving new backups.
          {state?.job && state.job.serverCount <= 1
            ? " It is the last server in the job, so the whole backup job is deleted."
            : ""}{" "}
          Existing backups on the backup storage are kept.
        </Text>
      ),
      labels: { confirm: "Remove", cancel: "Cancel" },
      confirmProps: { color: "red" },
      groupProps: { gap: "xs" },
      onConfirm: () => void removeFromBackups(),
    });
  }

  async function removeFromBackups() {
    try {
      await apiDelete(`/api/${resourceBase}/${vmid}/backups`);
    } catch (err) {
      if (!(isApiError(err) && err.status === 404 && err.error === "backup_job_not_found")) {
        notifications.show({
          color: "red",
          message: friendlyError(err, "Couldn't remove this server from backups."),
        });
        return;
      }
      // Already gone — same end state as a successful removal.
    }
    notifications.show({ color: "green", message: "Server removed from backups." });
    void load(false);
  }

  const job = state?.job ?? null;
  const member = state?.member === true;
  const scheduleLocked = job !== null && (job.hour === null || job.minute === null);
  const pausedAlert =
    job !== null && !job.enabled ? (
      <Alert color="yellow" variant="light" icon={<IconAlertTriangle size={18} />}>
        Backups paused by operator
      </Alert>
    ) : null;

  return (
    <Card padding="lg">
      <Stack gap="sm">
        <Group justify="space-between" align="center" wrap="nowrap">
          <Group gap="xs">
            <IconCloudUpload size={18} stroke={1.75} />
            <Title order={3}>Backups</Title>
          </Group>
          {member && job && (
            <Group gap="xs" wrap="nowrap">
              <Button
                variant="subtle"
                color="gray"
                size="xs"
                disabled={scheduleLocked}
                onClick={openScheduleModal}
              >
                Edit schedule
              </Button>
              <Button variant="subtle" color="gray" size="xs" onClick={confirmRemove}>
                Remove
              </Button>
            </Group>
          )}
        </Group>

        {loading ? (
          <Group justify="center" py="md">
            <Loader />
          </Group>
        ) : error ? (
          <Stack gap="xs">
            <Alert color="red" variant="light" icon={<IconAlertTriangle size={18} />}>
              Couldn't load backups. Proxmox may be unreachable — try again.
            </Alert>
            <div>
              <Button variant="default" size="xs" w={90} onClick={() => void load(true)}>
                Retry
              </Button>
            </div>
          </Stack>
        ) : member && job ? (
          <Stack gap="sm">
            <Text size="sm" c="dimmed">
              Daily at {scheduleLabel(job)} — {job.serverCount} server
              {job.serverCount === 1 ? "" : "s"} in the backup job, last backup{" "}
              {state.lastBackupAt ? formatBackupTime(state.lastBackupAt) : "never"}
            </Text>
            {scheduleLocked && (
              <Text size="xs" c="dimmed">
                Schedule managed in PVE console.
              </Text>
            )}
            {pausedAlert}
            {running && (
              <Group gap="xs">
                <Loader size="xs" />
                <Badge color="blue" variant="light">
                  Backup running…
                </Badge>
              </Group>
            )}
            {state.vmMissing ? (
              <Text c="dimmed" size="sm">
                This machine is no longer present on the cluster — its backups can't be listed.
              </Text>
            ) : state.backups.length === 0 ? (
              <Text c="dimmed" size="sm">
                No backups yet — the first one runs daily at {scheduleLabel(job)}.
              </Text>
            ) : (
              <Table.ScrollContainer minWidth={480}>
                <Table withTableBorder>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Time</Table.Th>
                      <Table.Th>Size</Table.Th>
                      <Table.Th>Status</Table.Th>
                      <Table.Th>Notes</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {state.backups.map((b: BackupRowSummary) => (
                      <Table.Tr key={b.id}>
                        <Table.Td>
                          <Group gap={4} wrap="nowrap">
                            {formatBackupTime(b.ctime)}
                            {b.protected && (
                              <Tooltip label="Protected" withArrow>
                                <IconLock
                                  size={12}
                                  stroke={1.75}
                                  style={{ cursor: "default" }}
                                  aria-label="Protected backup"
                                />
                              </Tooltip>
                            )}
                          </Group>
                        </Table.Td>
                        <Table.Td>{formatBytes(b.size)}</Table.Td>
                        <Table.Td>
                          <VerificationCell verificationState={b.verificationState} />
                        </Table.Td>
                        <Table.Td>
                          <Text size="xs" c="dimmed" truncate maw={220} title={b.notes}>
                            {b.notes ?? "—"}
                          </Text>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Table.ScrollContainer>
            )}
          </Stack>
        ) : job ? (
          <Stack gap="sm">
            {pausedAlert}
            <Alert color="blue" variant="light">
              Backups {job.enabled === false ? "are scheduled to run" : "run"} daily at{" "}
              {scheduleLabel(job)} — {job.serverCount} server
              {job.serverCount === 1 ? "" : "s"} included.
              <Group mt="xs">
                <Button
                  size="xs"
                  variant="default"
                  loading={adding}
                  onClick={() => void addToList()}
                >
                  Add this server to backups
                </Button>
              </Group>
            </Alert>
            {scheduleLocked && (
              <Text size="xs" c="dimmed">
                Schedule managed in PVE console.
              </Text>
            )}
          </Stack>
        ) : state?.storageConfigured ? (
          <Stack gap="sm" align="center" py="sm">
            <Text size="sm" c="dimmed">
              Automated daily backups to encrypted off-server storage.
            </Text>
            <Button variant="light" onClick={openEnableModal}>
              Enable Backups
            </Button>
          </Stack>
        ) : (
          <Text c="dimmed" size="sm">
            Backups not available for your account yet — contact support.
          </Text>
        )}
      </Stack>

      <Modal
        opened={enableOpen}
        onClose={() => setEnableOpen(false)}
        title={name ? `Enable backups — ${name}` : "Enable backups"}
        centered
      >
        <Stack gap="sm">
          <Group grow>
            <Select
              label="Hour"
              data={HOUR_OPTIONS}
              value={hourSel}
              onChange={(v) => setHourSel(v ?? "02")}
            />
            <Select
              label="Minute"
              data={MINUTE_OPTIONS}
              value={minuteSel}
              onChange={(v) => setMinuteSel(v ?? "00")}
            />
          </Group>
          <Text size="xs" c="dimmed">
            Runs daily at the chosen time (server time). Retention is managed by your
            operator.
          </Text>
          <Checkbox
            checked={runNow}
            onChange={(e) => setRunNow(e.currentTarget.checked)}
            label="Run first backup now"
            description="Backs up immediately — may take a while for large disks."
          />
          <Group justify="flex-end" mt="xs">
            <Button variant="default" onClick={() => setEnableOpen(false)} disabled={enabling}>
              Cancel
            </Button>
            <Button loading={enabling} onClick={() => void applyEnable()}>
              Apply
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={scheduleOpen}
        onClose={() => setScheduleOpen(false)}
        title={name ? `Edit backup schedule — ${name}` : "Edit backup schedule"}
        centered
      >
        <Stack gap="sm">
          <Group grow>
            <Select
              label="Hour"
              data={HOUR_OPTIONS}
              value={hourSel}
              onChange={(v) => setHourSel(v ?? "02")}
            />
            <Select
              label="Minute"
              data={MINUTE_OPTIONS}
              value={minuteSel}
              onChange={(v) => setMinuteSel(v ?? "00")}
            />
          </Group>
          <Group justify="flex-end" mt="xs">
            <Button
              variant="default"
              onClick={() => setScheduleOpen(false)}
              disabled={savingSchedule}
            >
              Cancel
            </Button>
            <Button loading={savingSchedule} onClick={() => void applySchedule()}>
              Apply
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Card>
  );
}

export function backupActionError(
  err: ApiError,
  fallback: string
): { title?: string; message: string } {
  if (err.error === "backup_storage_not_configured") {
    return { message: "Backups aren't set up for your account yet — contact support" };
  }
  if (err.error === "backup_job_exists") {
    return { message: "This server is already in your backup job" };
  }
  if (err.status === 502 && err.error === "pve_forbidden") {
    return { message: "Backup service isn't fully configured — contact support" };
  }
  if (err.status === 502 && err.error === "pve_unavailable") {
    return {
      title: "Proxmox unavailable",
      message: "Couldn't reach Proxmox. Try again in a moment.",
    };
  }
  return { message: friendlyError(err, fallback) };
}

function VerificationCell({ verificationState }: { verificationState?: string }) {
  if (verificationState === "ok") {
    return (
      <Text size="sm" c="green">
        ✓ verified
      </Text>
    );
  }
  if (verificationState === "failed") {
    return (
      <Text size="sm" c="red">
        verification failed
      </Text>
    );
  }
  return (
    <Text size="sm" c="dimmed">
      —
    </Text>
  );
}
